import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { getAllBanners } from "../../services/HomePage/BannerApi";
import { useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { FaChevronLeft, FaChevronRight, FaRocket, FaStar } from "react-icons/fa";

export default function NewBanner() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});
  const navigate = useNavigate();

  const BASE_IMAGE_URL = process.env.REACT_APP_BANNER_IMAGE_URL || "https://admintest.medrphalabs.com/BannerImage/";

  // Custom arrows – always visible
  const PrevArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute -left-3 md:-left-4 top-1/2 -translate-y-1/2 z-20 
      bg-white hover:bg-red-500 text-red-500 hover:text-white 
      shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
      opacity-70 hover:opacity-100 focus:outline-none"
      aria-label="Previous"
    >
      <FaChevronLeft size={14} />
    </button>
  );

  const NextArrow = ({ onClick }) => (
    <button
      onClick={onClick}
      className="absolute -right-3 md:-right-4 top-1/2 -translate-y-1/2 z-20 
      bg-white hover:bg-red-500 text-red-500 hover:text-white 
      shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
      opacity-70 hover:opacity-100 focus:outline-none"
      aria-label="Next"
    >
      <FaChevronRight size={14} />
    </button>
  );

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    variableWidth: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getAllBanners(5);
        const list = Array.isArray(data) ? data : [];
        const activeBanners = list.filter(
          (item) =>
            item.isActive === true &&
            item.image &&
            item.image.trim() !== ""
        );
        setBanners(activeBanners);
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  const handleBannerClick = (item) => {
    const id = Number(item.productCategoryId);
    if (id > 0) {
      navigate(`/brand/${id}`);
    }
  };

  const handleImageError = (index) => {
    setImgErrors((prev) => ({ ...prev, [index]: true }));
  };

  // Loading
  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
        <div className="flex justify-center gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-[220px] h-[260px] bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const validBanners = banners.filter((item) => item.image && item.image.trim() !== "");

  if (validBanners.length === 0) {
    return null;
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
      {/* Heading */}
      <div className="text-center mb-8">
        
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="text-gray-800">New</span>{" "}
          <span className="text-red-600">Arrivals</span>
        </h2>
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Discover our latest products and fresh collections
        </p>
      </div>

      {/* ✅ Slider – with full visibility (no clipping) */}
      <div className="relative group overflow-visible">
        <Slider {...settings} className="overflow-visible">
          {validBanners.map((item, i) => {
            const imageName = item.image;
            const imgUrl = `${BASE_IMAGE_URL}${encodeURIComponent(imageName)}`;
            const hasError = imgErrors[i];

            if (hasError) return null;

            return (
              // ✅ Extra padding and margin so the card is never cut
              <div key={item.id || i} className="px-2 py-4">
                <div
                  onClick={() => handleBannerClick(item)}
                  className={`relative w-[220px] h-[260px] 
                  rounded-xl overflow-hidden shadow-md hover:shadow-2xl 
                  transition-all duration-300 bg-white
                  ${Number(item.productCategoryId) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <img
                    src={imgUrl}
                    alt="Banner"
                    className="w-full h-full object-cover-fit"
                    onError={() => handleImageError(i)}
                    loading="lazy"
                  />

                  {/* Border effect - No shine animation */}
                  <div className="absolute inset-0 rounded-xl border-2 border-transparent hover:border-yellow-400/50 transition-all duration-300" />
                </div>
              </div>
            );
          })}
        </Slider>
      </div>

      <style>{`
        /* ✅ IMPORTANT: Make Slick List overflow visible so cards are never clipped */
        .slick-list {
          overflow: visible !important;
        }
        .slick-track {
          display: flex !important;
        }
        .slick-slide {
          display: flex;
          justify-content: center;
        }

        /* Pulse animation for loading */
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}